import React, { useEffect, useState } from "react";

import FeatherIcon from "../../common/FeatherIcon";
import NewMessageService from "../../../services/new-message.service";
import {
  getTeamThreadTitle,
  isTeamGroup,
  teamParticipantName,
} from "../team-thread";

const TeamDetails = ({
  thread,
  currentUserId,
  currentPersonalInboxId,
  onLabelUpdated,
}) => {
  const participants = thread?.participants || [];
  const isGroup = isTeamGroup(thread);
  const participantCount = Math.max(
    participants.length,
    thread?.participant_ids?.length || 0,
  );
  const threadId = thread?.id ?? thread?.thread_id;
  const iconSrc = isGroup
    ? "/images/private-message-team.svg"
    : "/images/private-message-individual.svg";
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(thread?.label || "");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    setEditingName(false);
    setDraftName(thread?.label || "");
    setSavingName(false);
    setNameError("");
  }, [threadId, thread?.label]);

  const cancelNameEdit = () => {
    if (savingName) return;
    setEditingName(false);
    setDraftName(thread?.label || "");
    setNameError("");
  };

  const saveGroupName = async (event) => {
    event.preventDefault();
    const label = draftName.trim();
    if (!label || savingName || !threadId) return;
    if (label === String(thread?.label || "").trim()) {
      setEditingName(false);
      return;
    }

    setSavingName(true);
    setNameError("");
    try {
      const response =
        await NewMessageService.updateTeamConversationLabel(
          threadId,
          label,
        );
      const savedLabel = response.data?.label || label;
      onLabelUpdated?.(threadId, savedLabel);
      setDraftName(savedLabel);
      setEditingName(false);
    } catch (error) {
      setNameError(
        error.response?.data?.error ||
          "The group name could not be updated.",
      );
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div className="hms-booking-info-wrapper team-thread-details">
      <div className="hms-guest-identity-card">
        <div className="message-text-group guest-info">
          <span
            className="message-text-group-icon team-thread-details-avatar"
            uk-tooltip={
              isGroup ? "Private team conversation" : "Private direct message"
            }
          >
            <img
              src={iconSrc}
              alt={isGroup ? "Private team group" : "Private direct message"}
            />
          </span>
          <div className="uk-width-expand">
            <div className="guest-chat-info">
              {isGroup && editingName ? (
                <form
                  className="team-thread-name-form"
                  onSubmit={saveGroupName}
                >
                  <input
                    className="uk-input team-thread-name-input"
                    type="text"
                    aria-label="Group name"
                    maxLength={120}
                    value={draftName}
                    onChange={(event) =>
                      setDraftName(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Escape") cancelNameEdit();
                    }}
                    disabled={savingName}
                    autoFocus
                  />
                  <div className="team-thread-name-actions">
                    <button
                      type="submit"
                      aria-label="Save group name"
                      title="Save group name"
                      disabled={!draftName.trim() || savingName}
                    >
                      {savingName ? (
                        <span uk-spinner="ratio: 0.45" />
                      ) : (
                        <FeatherIcon name="check" />
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="Cancel group name edit"
                      title="Cancel"
                      onClick={cancelNameEdit}
                      disabled={savingName}
                    >
                      <FeatherIcon name="x" />
                    </button>
                  </div>
                </form>
              ) : (
                <div className="team-thread-name-row">
                  <div className="chat-guest-value semi-bold-font">
                    {getTeamThreadTitle(
                      thread,
                      currentUserId,
                      currentPersonalInboxId,
                    )}
                  </div>
                  {isGroup && (
                    <button
                      className="team-thread-name-edit"
                      type="button"
                      aria-label="Edit group name"
                      title="Edit group name"
                      onClick={() => {
                        setDraftName(thread?.label || "");
                        setNameError("");
                        setEditingName(true);
                      }}
                    >
                      <FeatherIcon name="edit-2" />
                    </button>
                  )}
                </div>
              )}
              {nameError && (
                <div className="team-thread-name-error">
                  {nameError}
                </div>
              )}
              <div className="chat-guest-value team-thread-summary">
                {participantCount} participant
                {participantCount === 1 ? "" : "s"}
              </div>
              <div className="team-private-label">
                <FeatherIcon name="lock" />
                <span>Private</span>
              </div>
            </div>
          </div>
        </div>

        <div className="team-participant-list">
          <div className="team-participant-heading">Participants</div>
          {participants.map((participant) => (
            <div
              className="message-text-group guest-info team-participant-row"
              key={participant.personal_inbox_id}
            >
              <span className="team-message-avatar">
                {teamParticipantName(participant).charAt(0).toUpperCase()}
              </span>
              <div className="uk-width-expand">
                <div className="guest-chat-info">
                  <div className="chat-guest-value semi-bold-font">
                    {teamParticipantName(participant)}
                    {+participant.user_id === +currentUserId ? " (you)" : ""}
                  </div>
                  {participant.email && (
                    <div className="chat-guest-value team-participant-email">
                      {participant.email}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;
